import { Params, QRCodeColor, QRCodeFormat, QRCodeKind } from "@/types/utils";
import { triggerFileDownload } from "@/utils/helpers";
import { log } from "@/utils/logger";
import { SelectChangeEvent } from "@mui/material";
import { RefObject, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useModal } from "../context";
import api from "@/config/api";

interface QRCodeProps {
    format: QRCodeFormat;
    color: QRCodeColor;
    canvasRef: RefObject<HTMLCanvasElement>;
    parsedSvg: string | undefined;
}

const CANVAS_SIZE = 250;

const useDownloadQRCode = () => {
    const eid = useModal(state => state.product?.eid)!;
    const title = useModal(state => state.product?.longTitle)!;

    const onDownloadQRCode = (props: QRCodeProps) => {
        try {
            const { format } = props;
            if (!eid || !title) {
                throw new Error("Failed to locate product eid and title $sentry-ignore");
            }

            if (format === "svg") downloadSVG(props as Required<QRCodeProps>);
            if (format === "png") downloadPNG(props as Required<QRCodeProps>);
        } catch (e) {
            log.error(e, "onDownloadQRCodes");
        }
    };

    const trimTitle = (title: string) => {
        return title.replace(/ /g, "_");
    };

    const downloadPNG = (props: Required<QRCodeProps>) => {
        const { color, canvasRef } = props;
        const fileName = `${trimTitle(title)}_${eid}-${color}.png`;

        const url = canvasRef.current!.toDataURL("image/png");
        triggerFileDownload(url, fileName);
    };

    const downloadSVG = (props: Required<QRCodeProps>) => {
        const { color, parsedSvg } = props;
        const fileName = `${trimTitle(title)}_${eid}-${color}.svg`;

        if (!parsedSvg) {
            throw new Error("Failed to download QR code. File not found. $sentry-ignore");
        }

        const blob = new Blob([parsedSvg], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        triggerFileDownload(url, fileName);
        URL.revokeObjectURL(url);
    };

    return onDownloadQRCode;
};

const cache: Record<string, string> = {};

interface GetQRCode {
    format: QRCodeFormat;
    color: QRCodeColor;
    kind: QRCodeKind;
}

interface Settings {
    kind: QRCodeKind;
    format: QRCodeFormat;
    color: QRCodeColor;
    showInfo: boolean;
    showEnergy: boolean;
}

const useQRCode = () => {
    const { oid, bid } = useParams<Params>();
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const energy = useModal(state => state.product?.nutrition?.energy);
    const eid = useModal(state => state.product?.eid)!;
    const title = useModal(state => state.product?.longTitle)!;

    const [loading, setLoading] = useState(false);
    const [parsedSvg, setParsedSvg] = useState<string>();
    const [settings, setSettings] = useState<Settings>({
        kind: "eucompliant",
        format: "svg",
        color: "dark",
        showInfo: false,
        showEnergy: false,
    });

    const getQRCode = async ({ format, color, kind }: GetQRCode) => {
        try {
            if (!eid || !title) {
                throw new Error("Failed to locate product eid and title $sentry-ignore");
            }

            const key = `${eid}-${title}-${format}-${color}-${kind}`;
            const cached = cache[key];

            if (cached) {
                return cached;
            }

            setLoading(true);
            const qrCode = await api.get<{ qrString: string; variant: "dark" | "light" }>(
                `/organizations/${oid}/brands/${bid}/products/${eid}/qr-codes?format=${format}&color=${color}&kind=${kind}`,
            );

            cache[key] = qrCode.qrString;
            return qrCode.qrString;
        } catch (e) {
            log.error(e, "getQRCode");
        } finally {
            setLoading(false);
        }
    };

    const drawCanvas = (settings: Settings, svg: string) => {
        const { kind, color, showInfo, showEnergy } = settings;
        const isMarketing = kind === "marketing";

        const ctx = canvasRef.current!.getContext("2d")!;

        const textColor = color === "dark" ? "black" : "white";

        const image = new Image();

        image.src = `data:image/svg+xml;base64,${window.btoa(svg)}`;

        image.onload = () => {
            ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

            if (showInfo) {
                ctx.fillStyle = textColor;
                ctx.font = "bold 16px Figtree";
                ctx.textAlign = "center";
                ctx.fillText(isMarketing ? "Marketing" : "Ingredients", CANVAS_SIZE / 2, 28);
            }

            const centerX = CANVAS_SIZE / 2 - 225 / 2;
            const centerY = CANVAS_SIZE / 2 - 225 / 2;
            ctx.drawImage(image, centerX, centerY, 225, 225);

            if (showEnergy && !isMarketing && energy) {
                ctx.fillStyle = textColor;
                ctx.font = "bold 16px Figtree";
                ctx.textAlign = "center";
                ctx.fillText(
                    `100mL: E=${energy.kJ}kJ/${energy.kcal}kcal`,
                    CANVAS_SIZE / 2,
                    CANVAS_SIZE - 16,
                );
            }
        };
    };

    const addTextToSVG = (settings: Settings, svgString: string) => {
        const { kind, color, showInfo, showEnergy } = settings;
        const svgWithoutDeclaration = svgString
            .replace(/<\?xml[\s\S]*?\?>\s*/, "")
            .replace(/<!DOCTYPE[\s\S]*?>/i, "");

        const textColor = color === "dark" ? "black" : "white";
        const isMarketing = kind === "marketing";

        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgWithoutDeclaration, "image/svg+xml");

        const svgElement = svgDoc.documentElement;
        const viewBox = svgElement.getAttribute("viewBox")!.split(" ");
        const width = parseFloat(viewBox[2] as string);

        const centerX = width / 2;

        if (showInfo) {
            const infoEl = document.createElementNS("http://www.w3.org/2000/svg", "text");
            infoEl.setAttribute("x", centerX.toString());
            infoEl.setAttribute("y", "1.5");
            infoEl.setAttribute("font-family", "Figtree");
            infoEl.setAttribute("font-size", `${isMarketing ? "2.3" : "2"}`);
            infoEl.setAttribute("fill", `${textColor}`);
            infoEl.setAttribute("text-anchor", "middle");
            infoEl.setAttribute("dominant-baseline", "middle");
            infoEl.textContent = isMarketing ? "Marketing" : "Ingredients";
            infoEl.style.fontWeight = "bold";
            svgDoc.documentElement.appendChild(infoEl);
        }

        if (showEnergy && energy) {
            const energyEl = document.createElementNS("http://www.w3.org/2000/svg", "text");
            energyEl.setAttribute("x", centerX.toString());
            energyEl.setAttribute("y", "28");
            energyEl.setAttribute("font-family", "Figtree");
            energyEl.setAttribute("font-size", "2");
            energyEl.setAttribute("fill", `${textColor}`);
            energyEl.setAttribute("text-anchor", "middle");
            energyEl.setAttribute("dominant-baseline", "middle");
            energyEl.textContent = `100mL: E=${energy.kJ}kJ/${energy.kcal}kcal`;
            energyEl.style.fontWeight = "bold";
            svgDoc.documentElement.appendChild(energyEl);
        }

        const svgStringWithText = new XMLSerializer().serializeToString(svgDoc);
        setParsedSvg(svgStringWithText);
        return svgStringWithText;
    };

    const updateSettings = async (newSettings: Settings) => {
        const qrString = await getQRCode({
            format: newSettings.format,
            color: newSettings.color,
            kind: newSettings.kind,
        });

        if (newSettings.format === "svg") {
            addTextToSVG(newSettings, qrString!);
        } else {
            drawCanvas(newSettings, qrString!);
        }
    };

    const onChangeSettings = (
        e:
            | SelectChangeEvent<QRCodeKind | QRCodeFormat | QRCodeColor>
            | React.ChangeEvent<HTMLInputElement>,
    ) => {
        const { name, value, type, checked } = e.target as HTMLInputElement;

        if (type === "checkbox") {
            setSettings(prev => {
                const newSettings = { ...prev, [name]: checked };
                updateSettings(newSettings);
                return newSettings;
            });
        } else {
            setSettings(prev => {
                const newSettings = { ...prev, [name]: value };
                if (name === "kind") {
                    newSettings.showEnergy = false;
                }
                updateSettings(newSettings);
                return newSettings;
            });
        }
    };

    return {
        canvasRef,
        loading,
        settings,
        getQRCode,
        onChangeSettings,
        drawCanvas,
        parsedSvg,
        setParsedSvg,
        addTextToSVG,
    };
};

export { useDownloadQRCode, useQRCode };
